import React from "react";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { IconTrash, IconPencil, IconSettings, IconCalculator } from "@tabler/icons";
import {
  TextInput,
  Select,
  Checkbox,
  MultiSelect,
  Textarea,
} from "@mantine/core";
import RemoteSelect2 from "../../components/remoteSelect2";
import PointPayYearBands from "../../components/pointPayYearBands";
import PointExchangeRatePreview from "../../components/pointExchangeRatePreview";
import PointTypeTester from "../../components/pointTypeTester";
import { DATA_CURRENCY, DATA_ORDER_TYPE } from "../../data/options";

const apiEntity = "pointType";

const DATA_LINE_OF_BUSINESS = [
  { value: "LIFE", label: "LIFE" },
  { value: "GI", label: "GI" },
  { value: "MPF", label: "MPF" },
  { value: "INV", label: "INV" },
];

const DATA_ATTRIBUTION_MODES = [
  { value: "EQUAL_SPLIT", label: "Split equally" },
  { value: "FULL_TO_EACH", label: "Full amount to each consultant" },
  { value: "FIRST_ONLY", label: "First consultant only" },
];

const DATA_FORMULA_FACTORS = [
  { value: "YEAR_PREMIUM", label: "Year Premium" },
];

const FORMULA_FACTOR_LABELS = {
  YEAR_PREMIUM: "Year Premium",
};

const isYearPremium = (values) =>
  (values?.formulaFactor || "YEAR_PREMIUM") === "YEAR_PREMIUM";

const DEFAULT_PAY_YEAR_BANDS = [
  { minPayYears: 1, maxPayYears: 1, multiplier: 0.1 },
  { minPayYears: 2, maxPayYears: 2, multiplier: 0.3 },
  { minPayYears: 3, maxPayYears: 4, multiplier: 0.5 },
  { minPayYears: 5, maxPayYears: 9, multiplier: 1 },
  { minPayYears: 10, maxPayYears: 14, multiplier: 1.5 },
  { minPayYears: 15, maxPayYears: null, multiplier: 2 },
];

const tableSchema = {
  showActionColumn: true,
  columns: [
    { field: "code", headerName: "Code" },
    { field: "name", headerName: "Name" },
    {
      field: "formulaFactor",
      headerName: "Formula",
      cellRender: (col, row) =>
        FORMULA_FACTOR_LABELS[row.formulaFactor || "YEAR_PREMIUM"] ||
        row.formulaFactor ||
        "-",
    },
    {
      field: "baseCurrency",
      headerName: "Currency",
      cellRender: (col, row) => row.baseCurrency || "USD",
    },
    { field: "attributionMode", headerName: "Split" },
    { field: "status", headerName: "Status" },
    {
      field: "action",
      cellRender: ActionCellRender,
      isAction: true,
      actions: [
        {
          name: "edit",
          icon: IconPencil,
          props: {},
        },
      ],
      actionMenu: [
        {
          name: "delete",
          label: "Delete data",
          icon: IconTrash,
          props: {
            color: "red",
          },
        },
      ],
    },
  ],
  searchableFields: ["code", "name", "cname"],
};

const formSchema = {
  title: "Point Type",
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
    size: "xl",
  },
  apiEntity,
  initialValues: {
    code: "SP",
    name: "Standard Premium",
    cname: "標準保費",
    status: "ACTIVE",
    formulaFactor: "YEAR_PREMIUM",
    baseCurrency: "USD",
    attributionMode: "EQUAL_SPLIT",
    lineOfBusiness: ["LIFE"],
    orderTypes: ["NEW"],
    excludeRiders: true,
    productTypes: [],
    productCategories: [],
    providers: [],
    payYearBands: DEFAULT_PAY_YEAR_BANDS,
    remark: "",
  },
  layout: {
    containers: [
      {
        key: "main-tabs",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default",
          defaultValue: "Setting",
        },
        tab: [
          {
            key: "tab-setting",
            label: "Setting",
            icon: IconSettings,
          },
          {
            key: "tab-tester",
            label: "Tester",
            icon: IconCalculator,
          },
        ],
      },
      {
        key: "basic-section",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Basic Setting",
          description: "Name, status, and how points are split to consultants",
        },
      },
      {
        key: "basic-g",
        parent: "basic-section",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        props: {
          grow: true,
          gutter: 40,
        },
        cols: [
          { key: "basic-g1", props: { span: 6 } },
          { key: "basic-g2", props: { span: 6 } },
        ],
      },
      {
        key: "formula-section",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Formula",
          description:
            "Pick a formula factor. Year Premium annualizes modal premium, converts to the base currency when needed, then applies the pay-year multiplier",
        },
      },
      {
        key: "filter-section",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Order Filters",
          description: "Leave a filter empty to apply no restriction",
        },
      },
    ],
    fields: [
      {
        name: "code",
        parent: "basic-g1",
        component: TextInput,
        props: {
          required: true,
          label: "Code",
        },
      },
      {
        name: "name",
        parent: "basic-g1",
        component: TextInput,
        props: {
          required: true,
          label: "Name (Eng)",
        },
      },
      {
        name: "cname",
        parent: "basic-g1",
        component: TextInput,
        props: {
          label: "Name (Chi)",
        },
      },
      {
        name: "status",
        parent: "basic-g1",
        component: Select,
        props: {
          label: "Status",
          data: [
            { value: "ACTIVE", label: "ACTIVE" },
            { value: "INACTIVE", label: "INACTIVE" },
          ],
        },
      },
      {
        name: "attributionMode",
        parent: "basic-g2",
        component: Select,
        props: {
          required: true,
          label: "Consultant Split",
          data: DATA_ATTRIBUTION_MODES,
        },
      },
      {
        name: "excludeRiders",
        parent: "basic-g2",
        component: Checkbox,
        props: {
          label: "Exclude riders",
          type: "checkbox",
          mt: "xl",
        },
      },
      {
        name: "remark",
        parent: "basic-g2",
        component: Textarea,
        props: {
          label: "Remark",
          minRows: 2,
        },
      },
      {
        name: "formulaFactor",
        parent: "formula-section",
        component: Select,
        props: {
          required: true,
          label: "Formula Factor",
          data: DATA_FORMULA_FACTORS,
        },
      },
      {
        name: "baseCurrency",
        parent: "formula-section",
        component: Select,
        visibleOnly: isYearPremium,
        props: {
          required: true,
          label: "Base Currency",
          data: DATA_CURRENCY,
          searchable: true,
        },
      },
      {
        name: "exchangeRatePreview",
        parent: "formula-section",
        component: PointExchangeRatePreview,
        visibleOnly: isYearPremium,
        props: {},
      },
      {
        name: "payYearBands",
        parent: "formula-section",
        component: PointPayYearBands,
        visibleOnly: isYearPremium,
        props: {},
      },
      {
        name: "pointTypeTester",
        parent: "tab-tester",
        component: PointTypeTester,
        props: {},
      },
      {
        name: "lineOfBusiness",
        parent: "filter-section",
        component: MultiSelect,
        props: {
          label: "Line of Business",
          data: DATA_LINE_OF_BUSINESS,
          searchable: true,
          clearable: true,
        },
      },
      {
        name: "orderTypes",
        parent: "filter-section",
        component: MultiSelect,
        props: {
          label: "Order Types",
          data: DATA_ORDER_TYPE,
          searchable: true,
          clearable: true,
        },
      },
      {
        name: "productCategories",
        parent: "filter-section",
        component: RemoteSelect2,
        props: {
          label: "Product Categories",
          apiEntity: "productCategory",
          placeholder: "All",
          valueField: "_id",
          labelField: "name",
          labelRender: (data) => `${data.name} ${data.cname || ""}`,
          multiple: true,
          pageSize: 200,
          searchFields: ["name", "code", "cname"],
          clearable: true,
          sort: { by: "name", order: "asc" },
        },
      },
      {
        name: "productTypes",
        parent: "filter-section",
        component: RemoteSelect2,
        props: {
          label: "Product Types",
          apiEntity: "productType",
          placeholder: "All",
          valueField: "_id",
          labelField: "name",
          labelRender: (data) => `${data.name} ${data.cname || ""}`,
          multiple: true,
          pageSize: 200,
          searchFields: ["name", "code", "cname"],
          clearable: true,
          sort: { by: "name", order: "asc" },
        },
      },
      {
        name: "providers",
        parent: "filter-section",
        component: RemoteSelect2,
        props: {
          label: "Providers",
          apiEntity: "provider",
          placeholder: "All",
          valueField: "_id",
          labelField: "name",
          labelRender: (data) => `${data.name} ${data.cname || ""}`,
          multiple: true,
          pageSize: 200,
          searchFields: ["name", "code", "cname"],
          clearable: true,
          sort: { by: "name", order: "asc" },
        },
      },
    ],
  },
};

const PointType = () => {
  return (
    <PageList
      title={"Point Types"}
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
    />
  );
};

export default PointType;
